import type { HoverEvents, PressEvent } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols/index.d.ts";
import { type CSSProperties, forwardRef, type PropsWithChildren, type ReactElement, useMemo } from "react";
import type { AriaButtonProps } from "react-aria";
import { FocusRing, mergeProps, useButton, useHover } from "react-aria";

import { useTooltip } from "../../hooks.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { useDOMRef } from "../../hooks/useDomRef.ts";
import { useVisuallyLoading } from "../../hooks/useLoading.ts";
import type { TooltipAsProp } from "../../types/tooltip.d.ts";
import { Icon } from "../Icon/Icon.tsx";
import { LoadingSpinnerIntent } from "../ProgressSpinner/settings.ts";
import { AnimatedTooltip } from "../Tooltip/AnimatedTooltip.tsx";
import style from "./Buttons.module.css";
import { LoadingIcon } from "./LoadingIcon.tsx";
import { ActionButtonIconPlacement, ActionButtonIntent, ActionButtonSize } from "./settings.ts";
import { useAsyncLoading } from "./useAsyncLoading.ts";

export interface ActionButtonProps extends AriaButtonProps, HoverEvents {
  intent?: ActionButtonIntent;
  icon?: ReactElement | MaterialSymbol;
  iconPlacement?: ActionButtonIconPlacement;
  /**
   * Disable the button while loading, but keep it visually the same.
   * DO NOT activate `isDisabled` while loading.
   */
  isLoading?: boolean;
  /**
   * Tiny size is mostly used in Table/Grid cell.
   */
  size?: ActionButtonSize;
  tooltip?: TooltipAsProp | string;
  label?: string;
  /** HTML attributes. Usually the triggerProps from the hooks */
  className?: string;
  style?: CSSProperties;
  /**
   * In case the returned value is a Promise, the loading state will be automatically set
   * as long as the Promise is pending.
   */
  onPress?: (event: PressEvent) => unknown;
}

export const ActionButton = forwardRef<HTMLButtonElement, PropsWithChildren<ActionButtonProps>>(
  function ActionButton(props, ref) {
    const triggerRef = useDOMRef<HTMLButtonElement>(ref);

    const { isAsyncLoading, handlePress } = useAsyncLoading(props.onPress);

    // Fake loading for at least 1000ms
    const isLoading = useMemo(() => props.isLoading || isAsyncLoading, [isAsyncLoading, props.isLoading]);
    const isVisuallyLoading = useVisuallyLoading(isLoading);
    const isDisabled = useMemo(() => props.isDisabled || isVisuallyLoading, [isVisuallyLoading, props.isDisabled]);

    const { buttonProps, isPressed } = useButton({ ...props, isDisabled, onPress: handlePress }, triggerRef);
    const { hoverProps, isHovered } = useHover({ ...props, isDisabled });

    const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {
      isDisabled: props.tooltip == null,
      closeDelay: 0,
    });

    const dataAttributes = useDataAttributes(props);

    return (
      <>
        <FocusRing focusRingClass={style.focus}>
          <button
            className={clsx(
              style.button,
              {
                [style.button_primary]: props.intent == null || props.intent === ActionButtonIntent.Primary,
                [style.button_secondary]: props.intent === ActionButtonIntent.Secondary,
                [style.button_outlined]: props.intent === ActionButtonIntent.Outlined,
                [style.button_danger]: props.intent === ActionButtonIntent.Danger,
                [style.button_selected]: props.intent === ActionButtonIntent.Active,
                [style.pressed]: isPressed,
                [style.hover]: isHovered && !isLoading,
                [style.no_cursor]: isLoading,
                [style.disabled]: props.isDisabled, // while loading the styling remains the same
                [style.small]: props.size === ActionButtonSize.Small,
                [style.tiny]: props.size === ActionButtonSize.InTableCell,
              },
              props.className,
            )}
            type="button"
            data-intent={props.intent}
            {...dataAttributes}
            {...mergeProps(triggerProps, hoverProps, buttonProps)}
            data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}button`}
            ref={triggerRef}
            style={props.style}
          >
            {!!isVisuallyLoading && (
              <LoadingIcon
                intent={
                  props.isDisabled
                    ? LoadingSpinnerIntent.Secondary
                    : props.intent == null || props.intent === ActionButtonIntent.Primary
                      ? LoadingSpinnerIntent.Primary
                      : props.intent === ActionButtonIntent.Danger
                        ? LoadingSpinnerIntent.Danger
                        : undefined
                }
              />
            )}
            {props.iconPlacement !== ActionButtonIconPlacement.Right && props.icon != undefined && (
              <Icon
                icon={props.icon}
                className={clsx(style.icon, { [style.text_transparent]: isVisuallyLoading })}
                style={{ left: -1.5 }}
              />
            )}
            {!!(props.label || props.children) && (
              <span className={clsx(style.text, { [style.text_transparent]: isVisuallyLoading })}>
                {/* for compatibility reasons, but use label all the time */}
                {(props.label as string | undefined) ?? props.children}
              </span>
            )}
            {props.iconPlacement === ActionButtonIconPlacement.Right && props.icon != undefined && (
              <Icon
                icon={props.icon}
                className={clsx(style.icon, { [style.text_transparent]: isVisuallyLoading })}
                style={{ right: -1.5 }}
              />
            )}
          </button>
        </FocusRing>
        <AnimatedTooltip
          targetRef={triggerRef}
          title={(props.tooltip as TooltipAsProp | undefined)?.title}
          shortcut={(props.tooltip as TooltipAsProp | undefined)?.shortcut}
          intent={(props.tooltip as TooltipAsProp | undefined)?.intent}
          state={state}
          domProps={tooltipProps}
          nonInteractive
        >
          {typeof props.tooltip === "string" ? props.tooltip : props.tooltip?.content}
        </AnimatedTooltip>
      </>
    );
  },
);
